import { ChromeAppMessage, ChromeAppResponse } from 'Environment/ChromeApp/types';
import { clientType } from 'Environment/getClientType';
import { ChromeAndHtml5 } from 'GlobalAppConfigService/ChromeAppHtml5/common';
import { GacsHTMLChromeEventManager } from 'GlobalAppConfigService/ChromeAppHtml5/gacsCommonEventHandler';
import { validateAndFetchAppConfigurationUsingToken } from 'GlobalAppConfigService/fetchAppConfig';
import { Dependencies } from 'GlobalAppConfigService/types';
import {
	getStoreConfigUrlParams,
	getStoreUrl,
} from 'Workspace/SchemeCallHandler/schemeCallHandler';
import { shouldFetchGacsTokenForClientType } from 'Workspace/UserContext/utils';

export abstract class GacsHtmlChromeAppTransportBase {
	protected dependencies: Dependencies;
	protected readonly currentClientType: clientType;

	public constructor(dependencies: Dependencies, currentClientType: clientType) {
		if (!dependencies) {
			throw new Error('Dependencies are required');
		}
		if (!currentClientType) {
			throw new Error('Current client type is required');
		}
		this.dependencies = dependencies;
		this.currentClientType = currentClientType;
	}

	// Abstract method that derived classes must implement
	protected abstract postMessage(message: ChromeAppMessage): void;

	//Note this is an arrow function to ensure `this` context is preserved
	private sendMessage = async (message: ChromeAppMessage): Promise<ChromeAppResponse> => {
		return new Promise<ChromeAppResponse>((resolve, reject) => {
			try {
				if (!message?.payload?.correlationId) {
					reject(new Error('Correlation ID is required'));
					return;
				}
				GacsHTMLChromeEventManager.getInstance().set(
					message.payload.correlationId,
					resolve,
					reject
				);
				this.postMessage(message);
			} catch (error) {
				this.dependencies.logError(error);
				GacsHTMLChromeEventManager.getInstance().reject(
					message.payload.correlationId,
					error
				);
			}
		});
	};

	//Note this is an arrow function to ensure `this` context is preserved
	public sendAppConfigTokenRequiredReq =
		async (): Promise<ChromeAndHtml5.AppConfigTokenRequiredResponse> => {
			const message = ChromeAndHtml5.msgAppConfigTokenRequiredReq();
			return this.sendMessage(
				message
			) as Promise<ChromeAndHtml5.AppConfigTokenRequiredResponse>;
		};

	//Note this is an arrow function to ensure `this` context is preserved
	public sendAppConfigFetchWithTokenReq = async (
		tokenData: ChromeAndHtml5.TokenData
	): Promise<ChromeAppResponse> => {
		const message = ChromeAndHtml5.msgAppConfigFetchWithTokenReq(tokenData);
		return this.sendMessage(message);
	};

	// Token delivery channel used by validateAndFetchAppConfiguration.
	// Default: postMessage to the client. Subclasses (e.g. html5) may override to
	// deliver the token via another channel such as the SharedWorker Starter SDK.
	protected deliverAppConfigToken(tokenData: ChromeAndHtml5.TokenData): Promise<unknown> {
		return this.sendAppConfigFetchWithTokenReq(tokenData);
	}

	//Note this is an arrow function to ensure `this` context is preserved
	public validateAndFetchAppConfiguration = async () => {
		try {
			if (
				!shouldFetchGacsTokenForClientType(
					this.currentClientType,
					this.dependencies.getWSConfig()
				)
			) {
				return;
			}
			const urls: { gatewayUrl?: string; storeUrl?: string } =
				await getStoreConfigUrlParams(
					this.dependencies.getWSConfig()?.userInterface?.receiverConfiguration
						?.downloadURL
				);
			const storeurl = urls?.storeUrl || getStoreUrl();
			await validateAndFetchAppConfigurationUsingToken({
				storeurl,
				appConfigServiceTokenRequired: async () => {
					return await this.sendAppConfigTokenRequiredReq();
				},
				appConfigServiceFetchWithToken: tokenData =>
					this.deliverAppConfigToken(tokenData),
			});
		} catch (error) {
			this.dependencies.logError(error, {
				tags: {
					feature: 'GlobalAppConfigService',
				},
			});
		}
	};
}
